import styled, { css } from 'styled-components';
import { GalleryContainer } from '../../gallery';
import Spinner, { SpinnerText } from '../../spinner';
import { StickerContainer } from '../../sticker';
import { TagsContainer } from '../../tags';
import { pxToRem } from '../../../design-tokens/style-utilities';
import { ANWBDefault } from '../../../design-tokens/theme';
import { PanelExpertContainer } from '../components/PanelExpert/styles';
import { PanelLink, PanelLinksContainer } from '../components/PanelLinks';
import { PanelMediaContainer, PanelMediaLinkWrapper } from '../components/PanelMedia';
import { PanelStickerContainer } from '../components/PanelSticker';
const STICKER_OFFSET = 12;
const SPINNER_TOP = '35%';
export const panelLoadingStyles = () => css `
	position: relative;
	user-select: none;
	pointer-events: none;

	& > * {
		transition: opacity 0.2s;
		opacity: 0.2;
	}
`;
/**
 * PanelMain
 */
export const PanelMain = styled.div ``;
/**
 * PanelVerticalRhythm
 */
export const panelVerticalRhythmStyles = ({ theme }) => {
    return css `
		display: flex;
		flex-direction: column;
		height: 100%;

		${PanelMain} {
			/* Spacing on first and last child must be padding because margin wont work without a border */
			&
				> :first-child:not(${PanelMediaContainer}):not(${PanelMediaLinkWrapper}):not(
					${GalleryContainer}
				):not(${PanelStickerContainer}):not(${PanelLinksContainer}):not(${PanelExpertContainer}) {
				padding-top: ${pxToRem(theme.spacing[300])};
			}
			& > :first-child${PanelLinksContainer} {
				${PanelLink}:first-of-type {
					border-top: 0;
				}
			}
			& > :last-child:not(${PanelLinksContainer}):not(${PanelExpertContainer}) {
				padding-bottom: ${pxToRem(theme.spacing[300])};
			}
		}

		/* Only give PanelLinks top margin if it is not the only child */
		${PanelLinksContainer}:not(:only-child) {
			margin-top: ${pxToRem(theme.spacing[300])};
		}
	`;
};
/**
 * PanelWrapper
 */
export const panelWrapperStyles = ({ theme }) => css `
	&& {
		padding: ${pxToRem(Number(theme.spacing['300']) / 2)};

		@media (min-width: ${pxToRem(theme.viewportBreakpoint.md)}) {
			padding: ${pxToRem(Number(theme.spacing['400']) / 2)};
		}
	}
`;
export const PanelWrapper = styled.div `
	${panelWrapperStyles}
`;
/**
 * PanelInner
 */
export const PanelInner = styled.div `
	${panelVerticalRhythmStyles}
`;
/**
 * PanelContainer
 */
export const panelContainerStyles = ({ theme, variant, $hasMedia, $isLoading, }) => css `
	background-color: ${theme.colors.blanc.bgPanelInformative};
	border-radius: ${pxToRem(theme.borderRadius.m)};

	${TagsContainer} {
		margin: 0;
	}

	${variant === 'informative' &&
    css `
		border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.light.borderPanelInformative};
	`}

	${variant === 'featured' &&
    css `
		background-color: ${theme.name === ANWBDefault.name
        ? theme.colors.extralight.bgPanelAttention
        : theme.colors.extralight.bgPanelFeatured};

		${$hasMedia &&
        css `
			border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.extralight.borderPanelFeatured};

			${PanelLink} {
				margin-left: -${pxToRem(theme.borderWidth[2])};
				margin-right: -${pxToRem(theme.borderWidth[2])};
				padding-left: ${pxToRem(Number(theme.borderWidth[2]) + Number(theme.spacing['300']))};
				padding-right: ${pxToRem(Number(theme.borderWidth[2]) + Number(theme.spacing['300']))};
			}
		`}
	`}

	${variant === 'promotional' &&
    css `
		background-color: ${theme.colors.accent.bgPanelCrossSell};
		border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.accent.borderPanelCrossSell};

		&:hover {
			background-color: ${theme.colors.accent.hoverCrossSell};
		}

		${PanelLink} {
			border-color: ${theme.colors.blanc.dividerInverse};
		}
	`}

  ${$isLoading &&
    css `
		${panelLoadingStyles}

		${PanelInner} {
			min-height: ${pxToRem(120)};
		}
	`}
`;
export const panelContainerStickerStyles = ({ theme }) => css `
	${PanelStickerContainer} {
		float: right;
		right: -${pxToRem(STICKER_OFFSET + Number(theme.borderWidth['2']))};
		top: -${pxToRem(STICKER_OFFSET + Number(theme.borderWidth['2']))};
	}

	${PanelMediaContainer} + ${PanelStickerContainer}, ${PanelMediaLinkWrapper} + ${PanelStickerContainer} {
		float: none;
		right: auto;
		top: auto;

		${StickerContainer} {
			position: absolute;
			right: -${pxToRem(STICKER_OFFSET + Number(theme.borderWidth['2']))};
			transform: translateY(
				calc(-100% + ${pxToRem(Number(theme.spacing['200']) + Number(theme.borderWidth['2']))})
			);
		}
	}
`;
export const PanelContainer = styled.div `
	${panelContainerStyles}
	${panelContainerStickerStyles}
`;
/**
 * PanelPromotionContainer
 */
export const panelPromotionContainerStyles = ({ theme }) => css `
	/* calculation is 100% height minus the height of the border (:after) so on max height the border still fits in the container and is not placed below it */
	height: calc(100% - ${pxToRem(Number(theme.borderRadius.m) - Number(theme.borderWidth['2']))});

	&::after {
		content: '';
		box-sizing: content-box;
		display: block;
		height: ${pxToRem(Number(theme.borderRadius.m) - Number(theme.borderWidth['2']))};
		width: 100%;
		margin-bottom: ${pxToRem(Number(theme.borderWidth['2']) * -1)};
		margin-left: ${pxToRem(Number(theme.borderWidth['2']) * -1)};
		border: ${pxToRem(theme.borderWidth['2'])} solid ${theme.colors.accent.borderPanelPromotion};
		border-top: 0 none;
		border-bottom-left-radius: ${pxToRem(Number(theme.borderRadius.m) - Number(theme.borderWidth['2']))};
		border-bottom-right-radius: ${pxToRem(Number(theme.borderRadius.m) - Number(theme.borderWidth['2']))};
		background-color: ${theme.colors.accent.borderPanelPromotion};
	}
`;
export const PanelPromotionContainer = styled.div `
	${panelPromotionContainerStyles}
`;
/**
 * PanelAnchor
 */
export const panelAnchorStyles = () => css `
	text-decoration: none;
`;
export const PanelAnchor = styled.a `
	${panelAnchorStyles}
`;
/**
 * Panel Spinner
 */
export const panelSpinnerStyles = () => css `
	opacity: 1;
	position: absolute;
	top: ${SPINNER_TOP};
	left: 50%;
	transform: translateX(-50%);

	${SpinnerText} {
		white-space: nowrap;
	}
`;
export const PanelSpinner = styled(Spinner) `
	${panelSpinnerStyles}
`;
